import React, { useState } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/authContext';

import Signup from './pages/Signup';
import Login from './pages/Login';
import Home from './pages/Home';
import Debater from './pages/Debater';
import Modules from './pages/Modules';
import Settings from './pages/Settings';
import Training from './pages/Training';
import LessonPage from './pages/LessonPage';
import CreateLesson from './pages/CreateLesson';
import MyLessons from './pages/MyLessons';
import ClassroomMain from './pages/ClassroomMain';
import CreateClassroom from './pages/CreateClassroom';
import JoinClassroom from './pages/JoinClassroom';
import StudentProfile from './pages/StudentProfile';
import ManageClassroom from './pages/ManageClassroom';
import StudentClassroomPage from './pages/StudentClassroomPage';
import LessonFeedback from './pages/LessonFeedback';
import LessonFeedbackPage from './pages/LessonFeedbackPage';
import CreateModule from './pages/CreateModule';
import ManageModules from './pages/ManageModules';
import ModuleSettings from './pages/ModuleSettings';
import ModulePage from './pages/ModulePage';
import CreateModuleLesson from './pages/CreateModuleLesson';
import ModuleLessonPage from './pages/ModuleLessonPage';
import MyModuleLessons from './pages/MyModuleLessons';
import TrainingArgument from './pages/TrainingArgument';
import TrainingCharacterization from './pages/TrainingCharacterization';
import TrainingRefutation from './pages/TrainingRefutation';
import TrainingClashes from './pages/TrainingClashes';
import TrainingModeling from './pages/TrainingModeling';
import TrainingFraming from './pages/TrainingFraming';
import AboutUs from './pages/AboutUs';

function App() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();               // <-- added
  const isHome = location.pathname === '/';     // <-- added

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navLinkClass =
    'block px-4 py-2 rounded-md text-sm hover:bg-gray-200 transition';

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-md fixed top-0 left-0 right-0 z-30 h-16 flex items-center justify-between px-4 md:px-6">
        <button
          className="md:hidden text-gray-600 hover:text-gray-900"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          ☰
        </button>

        <div
          className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <div className="flex items-center space-x-2">
            <img src="/logo.png" alt="Logo" className="h-6 w-6" />
            <h1 className="text-xl font-bold text-blue-600">Debate Academy</h1>
          </div>
        </div>

        {user && (
          <div className="text-sm text-gray-600 ml-auto hidden md:block">
            {user.username} ({user.role})
            <button
              onClick={async () => {
                await logout();
                navigate('/');
              }}
              className="ml-4 px-3 py-1.5 rounded-lg text-sm bg-red-50 text-red-600 hover:bg-red-100"
            >
              Logout
            </button>
          </div>
        )}
      </header>

      {/* Layout */}
      <div className="flex flex-1 pt-16">
        {/* Sidebar */}
        <aside
          className={`fixed md:static top-16 left-0 w-64 h-[calc(100vh-4rem)] bg-gray-50 border-r border-gray-200 p-6 overflow-y-auto z-20 transition-transform duration-300 transform
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        >
          <nav className="space-y-8 text-gray-700 text-sm">
            <div>
              <p className="uppercase text-xs text-gray-400 mb-2">My Stuff</p>
              <NavLink to="/" onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >Home</NavLink>
              <NavLink to="/debater" onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >Debater</NavLink>
              <NavLink to="/modules" end onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >Modules</NavLink>
              <NavLink to="/classrooms" onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >Classrooms</NavLink>
              <NavLink to="/training" onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >Training</NavLink>
              <NavLink to="/modules/my-lessons" onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >My Lessons</NavLink>
              {(user?.role === 'teacher' || user?.isAdmin) && (
                <NavLink to="/modules/create" onClick={closeSidebar}
                  className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                >Create Lesson</NavLink>
              )}
            </div>

            {user?.isAdmin && (
              <div>
                <p className="uppercase text-xs text-gray-400 mb-2">Admin</p>
                <NavLink to="/admin/create-module" onClick={closeSidebar}
                  className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                >Create Module</NavLink>
                <NavLink to="/admin/manage-modules" onClick={closeSidebar}
                  className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                >Manage Modules</NavLink>
                <NavLink to="/admin/create-module-lesson" onClick={closeSidebar}
                  className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                >Create Module Lesson</NavLink>
                <NavLink to="/admin/my-module-lessons" onClick={closeSidebar}
                  className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                >Module Lessons</NavLink>
              </div>
            )}

            <div>
              <p className="uppercase text-xs text-gray-400 mb-2">My Account</p>
              <NavLink to="/settings" onClick={closeSidebar}
                className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >Settings</NavLink>
              {!user && (
                <>
                  <NavLink to="/signup" onClick={closeSidebar}
                    className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                  >Sign Up</NavLink>
                  <NavLink to="/login" onClick={closeSidebar}
                    className={({ isActive }) => `${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
                  >Login</NavLink>
                </>
              )}
              <NavLink to="/about"onClick={closeSidebar}
                  className={({ isActive }) =>`${navLinkClass} ${isActive ? 'bg-white font-semibold text-blue-600 border-l-4 border-blue-600 pl-3' : ''}`}
              >About
            </NavLink>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8">
          {/* Note: Home route renders without the white card */}
          <div className={`max-w-6xl mx-auto ${isHome ? '' : 'bg-white p-6 md:p-8 rounded-lg shadow-sm'}`}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/debater" element={<Debater />} />
              <Route path="/modules" element={<Modules />} />
              <Route path="/modules/view/:moduleId" element={<ModulePage />} />
              <Route path="/modules/view/:moduleId/lesson/:lessonId" element={<ModuleLessonPage />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/training" element={<Training />} />
              <Route path="/modules/:level/:lessonId" element={<LessonPage />} />
              <Route path="/modules/create" element={(user?.role === 'teacher' || user?.isAdmin) ? <CreateLesson /> : <p>Access denied.</p>} />
              <Route path="/modules/my-lessons" element={<MyLessons />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/classrooms" element={<ClassroomMain />} />
              <Route path="/classrooms/create" element={<CreateClassroom />} />
              <Route path="/classrooms/join" element={<JoinClassroom />} />
              <Route path="/profiles/:studentId" element={<StudentProfile />} />
              <Route path="/classrooms/manage/:classroomId" element={<ManageClassroom />} />
              <Route path="/classrooms/student/:classroomId" element={<StudentClassroomPage />} />
              <Route path="/classrooms/student/:classroomId/lesson/:lessonId" element={<LessonPage />} />
              <Route path="/feedback/:classroomId/:lessonId/:studentId" element={<LessonFeedback />} />
              <Route path="/classrooms/:classroomId/lesson/:lessonId/feedback/:studentId" element={<LessonFeedbackPage />} />
              <Route path="/admin/create-module" element={user?.isAdmin ? <CreateModule /> : <p>Access denied.</p>} />
              <Route path="/admin/manage-modules" element={user?.isAdmin ? <ManageModules /> : <p>Access denied.</p>} />
              <Route path="/admin/create-module-lesson" element={user?.isAdmin ? <CreateModuleLesson /> : <p>Access denied.</p>} />
              <Route path="/admin/my-module-lessons" element={user?.isAdmin ? <MyModuleLessons /> : <p>Access denied.</p>} />
              <Route path="/admin/module-settings/:moduleId" element={user?.isAdmin ? <ModuleSettings /> : <p>Access denied.</p>} />
              <Route path="/training/argument" element={<TrainingArgument />} />
              <Route path="/training/characterization" element={<TrainingCharacterization />} />
              <Route path="/training/refutation" element={<TrainingRefutation />} />
              <Route path="/training/clashes" element={<TrainingClashes />} />
              <Route path="/training/modeling" element={<TrainingModeling />} />
              <Route path="/training/framing" element={<TrainingFraming />} />
              <Route path="/about" element={<AboutUs />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
